import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { X, Camera, Upload, Check, ClipboardList, Save } from "lucide-react";
import { Button } from "@/components/ui/button";

export interface PesquisaMercadoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const acoes = ["Desconto", "Brinde", "Combo", "Exposição diferenciada"];

export function PesquisaMercadoModal({ open, onOpenChange }: PesquisaMercadoModalProps) {
  const [temAcao, setTemAcao] = useState<"sim" | "nao" | "">("");
  const [selecionadas, setSelecionadas] = useState<string[]>([]);
  const [preco, setPreco] = useState("");
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const toggleAcao = (a: string) =>
    setSelecionadas((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]));

  const formatPreco = (v: string) => {
    const n = v.replace(/\D/g, "");
    if (!n) return "";
    const num = (parseInt(n, 10) / 100).toFixed(2);
    return num.replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  };

  const handleFile = (file: File | null) => {
    setArquivo(file);
    if (preview) {
      URL.revokeObjectURL(preview);
      setPreview(null);
    }
    if (file && file.type.startsWith("image/")) {
      setPreview(URL.createObjectURL(file));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-md overflow-hidden rounded-2xl border-none p-0 shadow-2xl [&>button]:hidden">
        <div className="relative overflow-hidden bg-gradient-to-br from-tudo-blue via-tudo-blue to-tudo-blue/80 px-5 py-4 sm:px-6">
          <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/10 blur-xl" />
          <div className="pointer-events-none absolute -bottom-12 -left-6 h-28 w-28 rounded-full bg-tudo-green/20 blur-xl" />
          <div className="relative flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15 shadow-inner ring-1 ring-white/20 backdrop-blur-sm">
                <ClipboardList className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0">
                <DialogTitle className="truncate text-left text-lg font-bold tracking-wide text-white">
                  Pesquisa Mercado
                </DialogTitle>
                <p className="truncate text-xs font-medium text-white/70">
                  Concorrência e preços
                </p>
              </div>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="shrink-0 rounded-lg p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
              aria-label="Fechar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="max-h-[70vh] space-y-4 overflow-y-auto bg-muted/30 p-4 sm:p-5">
          {/* Pergunta 1 */}
          <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
            <div className="flex items-center gap-2.5 border-b border-border bg-muted/40 px-4 py-2.5">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-tudo-blue text-xs font-bold text-white">1</span>
              <h3 className="text-xs font-bold uppercase tracking-wide text-tudo-blue">
                Há ações promocionais da concorrência? <span className="text-tudo-red">*</span>
              </h3>
            </div>
            <div className="grid grid-cols-2 gap-3 p-4">
              {(["sim", "nao"] as const).map((v) => {
                const ativo = temAcao === v;
                const isSim = v === "sim";
                return (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setTemAcao(v)}
                    className={`flex items-center justify-center gap-2 rounded-xl border-2 px-4 py-3 text-sm font-bold transition-all ${
                      ativo
                        ? isSim
                          ? "border-tudo-green bg-tudo-green text-white shadow-md shadow-tudo-green/20"
                          : "border-tudo-red bg-tudo-red text-white shadow-md shadow-tudo-red/20"
                        : "border-border bg-background text-muted-foreground hover:border-tudo-blue/50 hover:text-tudo-blue"
                    }`}
                  >
                    {isSim ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
                    {isSim ? "Sim" : "Não"}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Pergunta 2 */}
          <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
            <div className="flex items-center gap-2.5 border-b border-border bg-muted/40 px-4 py-2.5">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-tudo-blue text-xs font-bold text-white">2</span>
              <h3 className="text-xs font-bold uppercase tracking-wide text-tudo-blue">
                Quais ações foram identificadas?
              </h3>
            </div>
            <div className="space-y-2 p-3">
              {acoes.map((a) => {
                const ativo = selecionadas.includes(a);
                return (
                  <button
                    key={a}
                    type="button"
                    onClick={() => toggleAcao(a)}
                    className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-all ${
                      ativo
                        ? "border-tudo-blue bg-tudo-blue/5 shadow-sm"
                        : "border-border bg-card hover:border-tudo-blue/40 hover:bg-tudo-blue/5"
                    }`}
                  >
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors ${
                        ativo ? "border-tudo-blue bg-tudo-blue" : "border-border bg-background"
                      }`}
                    >
                      {ativo && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
                    </span>
                    <span className={`text-sm font-medium ${ativo ? "text-tudo-blue" : "text-foreground"}`}>{a}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Pergunta 3 */}
          <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
            <div className="flex items-center gap-2.5 border-b border-border bg-muted/40 px-4 py-2.5">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-tudo-blue text-xs font-bold text-white">3</span>
              <h3 className="text-xs font-bold uppercase tracking-wide text-tudo-blue">
                Valor da Losartana do concorrente
              </h3>
            </div>
            <div className="p-4">
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-tudo-blue">
                  R$
                </span>
                <input
                  inputMode="numeric"
                  value={preco}
                  onChange={(e) => setPreco(formatPreco(e.target.value))}
                  placeholder="0,00"
                  className="h-11 w-full rounded-lg border border-border bg-background pl-10 pr-3 text-right text-base font-semibold text-foreground outline-none transition-colors focus:border-tudo-blue focus:ring-2 focus:ring-tudo-blue/30"
                />
              </div>
            </div>
          </div>

          {/* Pergunta 4 */}
          <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
            <div className="flex items-center gap-2.5 border-b border-border bg-muted/40 px-4 py-2.5">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-tudo-blue text-xs font-bold text-white">4</span>
              <h3 className="text-xs font-bold uppercase tracking-wide text-tudo-blue">
                Registrar evidência da concorrência
              </h3>
            </div>
            <div className="space-y-3 p-4">
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => cameraRef.current?.click()}
                  className="flex items-center justify-center gap-2 rounded-lg border border-tudo-blue bg-tudo-blue px-3 py-2.5 text-sm font-semibold text-white transition-all hover:bg-tudo-blue/90"
                >
                  <Camera className="h-4 w-4" />
                  Tirar foto
                </button>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="flex items-center justify-center gap-2 rounded-lg border border-tudo-blue bg-background px-3 py-2.5 text-sm font-semibold text-tudo-blue transition-all hover:bg-tudo-blue/5"
                >
                  <Upload className="h-4 w-4" />
                  Escolher arquivo
                </button>
              </div>

              <input
                ref={cameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
              />
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
              />

              {arquivo ? (
                <div className="overflow-hidden rounded-xl border border-border">
                  {preview && (
                    <img src={preview} alt="Prévia da evidência" className="h-40 w-full object-cover" />
                  )}
                  <p className="flex items-center gap-1.5 truncate bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                    <Check className="h-3.5 w-3.5 text-tudo-green" />
                    <span className="font-medium text-foreground">{arquivo.name}</span>
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-border bg-muted/30 py-6 text-center">
                  <Camera className="h-7 w-7 text-muted-foreground/50" />
                  <p className="text-xs font-medium text-muted-foreground">
                    Nenhuma evidência anexada
                  </p>
                </div>
              )}
            </div>
          </div>

          <Button
            type="button"
            disabled={!temAcao}
            onClick={() => onOpenChange(false)}
            className="flex h-11 w-full items-center justify-center gap-2 bg-gradient-to-r from-tudo-green to-tudo-green/85 text-sm font-semibold uppercase tracking-wide text-white shadow-md shadow-tudo-green/20 transition-all hover:-translate-y-0.5 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-md"
          >
            <Save className="h-4 w-4" />
            Salvar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
